import React, { useState, useEffect } from 'react';
import AddToProjectsButton from './AddToProjectsButton';
import logger from '../utils/logger';

const Measures = () => {
  const [measures, setMeasures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');

  useEffect(() => {
    fetchMeasures();
  }, [selectedCategory, selectedIndustry]);

  const fetchMeasures = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedIndustry) params.append('industry', selectedIndustry);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/measures?${params}`);
      const data = await response.json();
      setMeasures(data.measures || []);
    } catch (error) {
      logger.error('Error fetching measures:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfidenceColor = (level) => {
    switch (level) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const categories = ['All', 'Lighting', 'Motors', 'HVAC', 'Compressed Air', 'Steam Systems', 'Process Optimization'];
  const industries = ['All', 'Food & Beverage', 'Metal Products', 'Chemicals & Pharmaceuticals', 'Plastics & Packaging'];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Energy Efficiency Measures</h1>
          <p className="text-gray-600">Explore recommended energy efficiency improvements and add them to your projects</p>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value === 'All' ? '' : e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value === 'All' ? '' : e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>
            <div className="ml-auto">
              <button
                onClick={fetchMeasures}
                disabled={isLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Measures Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {measures.length === 0 ? (
            <div className="col-span-full text-center py-12">
              {isLoading ? (
                <div className="text-gray-500">Loading measures...</div>
              ) : (
                <div className="text-gray-500">No measures found. Try adjusting your filters.</div>
              )}
            </div>
          ) : (
            measures.map((measure) => (
              <div key={measure.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{measure.title}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceColor(measure.confidence_level)}`}>
                      {measure.confidence_level}
                    </span>
                  </div>

                  {/* Category */}
                  {measure.category && (
                    <div className="mb-3">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {measure.category}
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{measure.description}</p>

                  {/* Metrics */}
                  <div className="space-y-2 mb-4">
                    {measure.savings_potential && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Annual Savings:</span>
                        <span className="text-sm font-medium text-green-600">
                          ${measure.savings_potential.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {measure.implementation_cost && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Implementation Cost:</span>
                        <span className="text-sm font-medium text-gray-900">
                          ${measure.implementation_cost.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {measure.payback_years && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Payback Period:</span>
                        <span className="text-sm font-medium text-blue-600">
                          {measure.payback_years} years
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ROI Indicator */}
                  {measure.payback_years && (
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>ROI Indicator</span>
                        <span>
                          {measure.payback_years <= 2 ? 'Excellent' : 
                           measure.payback_years <= 4 ? 'Good' : 
                           measure.payback_years <= 7 ? 'Fair' : 'Long-term'}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            measure.payback_years <= 2 ? 'bg-green-500' :
                            measure.payback_years <= 4 ? 'bg-blue-500' :
                            measure.payback_years <= 7 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ 
                            width: `${Math.max(10, Math.min(100, 100 - (measure.payback_years * 10)))}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Add to Projects Button */}
                  <div className="pt-4 border-t border-gray-200">
                    <AddToProjectsButton
                      measure={measure}
                      assessmentId="demo-assessment-id"
                      onSuccess={(data) => {
                        logger.log('Measure added to projects:', data);
                      }}
                      className="w-full justify-center"
                      variant="primary"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                About Energy Efficiency Measures
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  These measures are based on industry best practices and are tailored to specific industrial sectors. 
                  Click "Add to Projects" to track implementation progress, assign contractors, and link to available grants.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Measures;